import { Link } from 'react-router-dom';
import { useMetricsV2Api } from '@data/hooks/api/use-metrics-v2-api/use-metrics-v2-api';
import { formatMetric } from '@data/utils/format-metric';
import type { PartialMetricV2Request } from '@data/pages/ProductLandingPage/ProductLandingPage';
import { LoadingBar } from '@data/components/basic/LoadingBar/LoadingBar';
import { getEdApps } from '@data/utils/get-ed-apps';

// ── Sub-components ─────────────────────────────────────────────────────────────

function MetricDisplay({
  label,
  value,
  loading = true,
}: {
  label: string;
  value: string;
  loading?: boolean;
}) {
  return (
    <div className="flex-1">
      {loading ? (
        <LoadingBar className="h-16" />
      ) : (
        <div className="gap-8">
          <p className="mb-[2px] text-[14px] tablet:text-[16px]">{label}</p>
          <p className="text-2xl tablet:text-3xl">{value}</p>
        </div>
      )}
    </div>
  );
}

// ── Styles ─────────────────────────────────────────────────────────────────────

const CARD_BASE_STYLES =
  'rounded-lg border border-solid border-[#D4D4D4] bg-white px-4 py-6 shadow-md tablet:p-8';

// ── Component ──────────────────────────────────────────────────────────────────

interface ProductCardProps {
  applicationCode: string;
  applicationLabel: string;
  applicationIconSrc: string;
  gradeDisplay?: string;
  sharedMetricProps: PartialMetricV2Request;
}

export function ProductCard({
  applicationCode,
  applicationLabel,
  applicationIconSrc,
  gradeDisplay,
  sharedMetricProps,
}: ProductCardProps) {
  const { data: sessionsData, isFetched: sessionsFetched } = useMetricsV2Api({
    ...sharedMetricProps,
    edApps: getEdApps(applicationCode),
    metricType: 'TOTAL_STUDENT_SESSIONS',
  });

  const { data: minutesData, isFetched: minutesFetched } = useMetricsV2Api({
    ...sharedMetricProps,
    edApps: getEdApps(applicationCode),
    metricType: 'TOTAL_STUDENT_MINUTES',
  });

  const loading = !sessionsFetched || !minutesFetched;
  const sessions = formatMetric(sessionsData?.value);
  const minutes = formatMetric(minutesData?.value);
  const hasNoData =
    sessionsFetched && (!sessionsData?.value || sessionsData.value === '0');

  const cardContent = (
    <>
      {loading ? (
        <div role="status" aria-label="Loading">
          <LoadingBar className="mb-4 h-10 w-28" />
          <LoadingBar className="mb-3 h-4 w-16" />
          <LoadingBar className="mb-2 h-4" />
        </div>
      ) : (
        <>
          <div className="mb-4 h-10">
            <img
              src={applicationIconSrc}
              alt={`${applicationLabel} icon`}
              className="h-full object-contain"
            />
          </div>
          <h3 className="mb-[2px] text-2xl font-semibold text-[#333] tablet:text-xl">
            {applicationLabel}
          </h3>
          {gradeDisplay && (
            <p className="text-sm font-semibold text-[#333]">
              Grade{' '}
              <span className="text-[16px] font-normal">{gradeDisplay}</span>
            </p>
          )}
        </>
      )}

      {hasNoData ? (
        <div className="mt-[10px] rounded border border-dashed border-neutral-400 p-2">
          <p className="text-xs font-bold leading-[18px] text-neutral-700">
            No product data available
          </p>
          <p className="text-xs leading-[18px] text-neutral-700">
            Data will automatically be populated as students use the product
            digitally.
          </p>
        </div>
      ) : (
        <div className="mt-[10px] flex gap-8 font-semibold text-neutral-1300">
          <MetricDisplay
            label="Student Sessions"
            value={sessions}
            loading={loading}
          />
          <MetricDisplay
            label="Student Minutes"
            value={minutes}
            loading={loading}
          />
        </div>
      )}
    </>
  );

  if (loading || hasNoData) {
    return (
      <div className={`cursor-default ${CARD_BASE_STYLES}`}>{cardContent}</div>
    );
  }

  return (
    <Link
      to={`/data/${applicationCode}/overview`}
      className={`${CARD_BASE_STYLES} transition-shadow hover:shadow-lg`}
    >
      {cardContent}
    </Link>
  );
}
